import React from "react";
import { motion } from "framer-motion";
import {
  Target,
  TrendingUp,
  Gauge,
  Zap,
  Crosshair,
  LineChart,
  Database,
  Sparkles,
} from "lucide-react";

const SERVICES = [
  {
    no: "01",
    icon: Target,
    title: "Demand Generation",
    body: "Full-funnel B2B programs — content, nurture, webinars, syndication — that fill pipeline with buyers, not clicks.",
  },
  {
    no: "02",
    icon: TrendingUp,
    title: "Growth Marketing",
    body: "Experimentation frameworks, channel diversification and lifecycle loops tuned for compounding revenue.",
  },
  {
    no: "03",
    icon: Gauge,
    title: "Performance Marketing",
    body: "PPC across LinkedIn, Google, Meta, Bing, Adroll — bid strategy, creative, landing pages, CRO — measured on CAC & ROAS.",
  },
  {
    no: "04",
    icon: Crosshair,
    title: "Account-Based Marketing",
    body: "Target account orchestration with 6sense & Rollworks — signals, plays, sales alignment, multi-threaded outreach.",
  },
  {
    no: "05",
    icon: Zap,
    title: "Marketing Automation",
    body: "HubSpot & Marketo architecture — scoring, workflows, attribution, data hygiene — built for scale and clarity.",
  },
  {
    no: "06",
    icon: Database,
    title: "CRM & RevOps",
    body: "Salesforce integration, funnel stages, lead routing, SLAs — so marketing and sales speak the same language.",
  },
  {
    no: "07",
    icon: LineChart,
    title: "Analytics & Attribution",
    body: "GA4, dashboards and multi-touch attribution — turning data into the story that steers the next quarter.",
  },
  {
    no: "08",
    icon: Sparkles,
    title: "AI & Agentic Marketing",
    body: "Claude, GPT-5 and MCP-driven agents wired into the stack — content ops, lead enrichment, reporting and campaign QA that ship real output, not demos.",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-14 md:py-20 bg-paper">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-4 mb-8 md:mb-12">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">§ 02 — Expertise</span>
          <span className="flex-1 h-px bg-line" />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">8 Practices</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-12">
          <h2 className="md:col-span-7 font-heading font-black text-5xl md:text-7xl leading-[0.9] tracking-tightest">
            Full-stack B2B marketing, <span className="text-signal">engineered for pipeline.</span>
          </h2>
          <p className="md:col-span-5 md:pt-3 text-base md:text-lg text-ink2 leading-relaxed">
            I don&apos;t run channels in isolation. I design systems where demand, automation, ABM and analytics
            reinforce each other — so every dollar has a seat at the revenue table.
          </p>
        </div>

        <div className="border border-ink grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const isLastCol = (i + 1) % 4 === 0;
            const rowStart = Math.floor(i / 4);
            const totalRows = Math.ceil(SERVICES.length / 4);
            const isLastRow = rowStart === totalRows - 1;
            return (
              <motion.div
                key={s.no}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.04, ease: [0.2, 0.7, 0.2, 1] }}
                data-testid={`service-card-${s.no}`}
                className={`svc-card group relative p-8 md:p-10 bg-paper hover:bg-surface border-ink ${
                  !isLastCol ? "lg:border-r" : ""
                } ${!isLastRow ? "border-b" : ""} ${
                  (i + 1) % 2 === 0 ? "sm:border-l-0" : "sm:border-r lg:border-r"
                }`}
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">
                    {s.no}
                  </span>
                  <Icon
                    className="w-6 h-6 text-ink group-hover:text-signal transition-colors"
                    strokeWidth={1.5}
                  />
                </div>
                <h3 className="font-heading font-bold text-2xl leading-tight tracking-tight mb-4">
                  {s.title}
                </h3>
                <p className="text-[14px] leading-relaxed text-ink2">{s.body}</p>
                <span className="mt-8 inline-block h-[2px] w-0 bg-signal group-hover:w-12 transition-all duration-500" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
